import React, { useState } from 'react'
import { ArrowRight, BadgeCheck, CheckCircle2, FileSearch, ShieldCheck, Sparkles } from 'lucide-react'

const HERO_IMAGE = '/category-landing-assets/generated/hidden-insurance-money-financial-desk.png'

const progressSteps = [
  { title: '정보 입력', body: '이름과 연락처, 인증에 필요한 기본 정보를 확인합니다.', icon: FileSearch },
  { title: '본인 인증', body: '고객 동의 후 간편인증으로 조회를 시작합니다.', icon: ShieldCheck },
  { title: '일부 결과 확인', body: '미청구 가능성 요약만 먼저 안내합니다.', icon: Sparkles },
]

const resultItems = [
  { label: '점검 기간', value: '최근 3개년' },
  { label: '확인 상태', value: '미청구 가능성 있음' },
  { label: '예상 범위', value: '상담 후 상세 확인' },
]

export default function HiddenInsuranceMoneyLandingPage() {
  const [step, setStep] = useState('form')

  const handleSubmit = (event) => {
    event.preventDefault()
    setStep('result')
  }

  const showResult = step === 'result'

  return (
    <main className="min-h-screen bg-white text-slate-950">
      <section className="relative isolate min-h-[72vh] overflow-hidden bg-slate-950 px-4 py-16 text-white sm:px-6 lg:px-8">
        <img
          src={HERO_IMAGE}
          alt=""
          className="absolute inset-0 -z-20 h-full w-full object-cover"
        />
        <div className="absolute inset-0 -z-10 bg-slate-950/72" />
        <div className="mx-auto flex min-h-[56vh] max-w-6xl flex-col justify-center">
          <div className="max-w-3xl">
<h1 className="text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
              숨은보험금찾기
            </h1>
            <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-100">
              의료비와 보험금 수령 자료를 바탕으로 놓쳤을 수 있는 청구 가능성을 점검하고, 고객이 상담을 원할 때만 리드로 전환합니다.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#lookup-form"
                className="inline-flex items-center gap-2 rounded bg-white px-5 py-3 text-sm font-bold text-slate-950 shadow-sm transition hover:bg-sky-50"
              >
                점검 시작
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </a>
              <a
                href="#landing-result"
                className="inline-flex items-center gap-2 rounded border border-white/35 px-5 py-3 text-sm font-bold text-white transition hover:bg-white/10"
              >
                결과 예시 보기
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-slate-50 px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-6xl gap-4 md:grid-cols-3">
          {progressSteps.map((item) => {
            const Icon = item.icon
            return (
              <article key={item.title} className="rounded border border-slate-200 bg-white p-5 shadow-sm">
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded bg-sky-100 text-sky-800">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </div>
                <h2 className="text-base font-bold text-slate-950">{item.title}</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">{item.body}</p>
              </article>
            )
          })}
        </div>
      </section>

      <section id="lookup-form" className="px-4 py-14 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-6xl gap-8 lg:grid-cols-[minmax(0,1fr)_24rem]">
          <div>
            <p className="text-sm font-bold text-sky-700">조회 전 확인</p>
            <h2 className="mt-2 text-3xl font-bold tracking-normal text-slate-950">간단한 정보 입력 후 인증으로 이어집니다</h2>
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {['민감정보 동의 분리', '결과 일부만 노출', '상담 동의 후 리드 저장', '원천 데이터 단기 보관'].map((item) => (
                <div key={item} className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden="true" />
                  {item}
                </div>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="rounded border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="text-lg font-bold text-slate-950">숨은보험금 점검</h3>
            <div className="mt-5 space-y-4">
              <label className="block text-sm font-semibold text-slate-700">
                이름
                <input className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-base outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100" placeholder="홍길동" />
              </label>
              <label className="block text-sm font-semibold text-slate-700">
                연락처
                <input className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-base outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100" placeholder="01012345678" inputMode="tel" />
              </label>
              <label className="block text-sm font-semibold text-slate-700">
                인증 수단
                <select className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-base outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-100" defaultValue="KAKAO">
                  <option value="KAKAO">카카오 간편인증</option>
                  <option value="SMS">SMS 인증</option>
                </select>
              </label>
              <label className="flex items-start gap-2 rounded border border-slate-200 bg-slate-50 p-3 text-sm leading-6 text-slate-700">
                <input type="checkbox" className="mt-1" required />
                개인정보, 민감정보, 개인신용정보 조회 및 처리에 동의합니다.
              </label>
            </div>
            <button type="submit" className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded bg-slate-950 px-4 py-3 text-sm font-bold text-white transition hover:bg-sky-800">
              인증 후 결과 보기
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </form>
        </div>
      </section>

      <section id="landing-result" className="bg-slate-950 px-4 py-14 text-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="max-w-2xl">
            <p className="text-sm font-bold text-amber-200">결과 일부 조회</p>
            <h2 className="mt-2 text-3xl font-bold tracking-normal">상세 내역은 상담 신청 후 확인합니다</h2>
          </div>
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {resultItems.map((item) => (
              <article key={item.label} className="rounded border border-white/15 bg-white/10 p-5">
                <p className="text-sm font-semibold text-slate-300">{item.label}</p>
                <p className="mt-3 text-2xl font-bold">{showResult ? item.value : '인증 후 표시'}</p>
              </article>
            ))}
          </div>
          <div className="mt-8 flex flex-col gap-4 rounded border border-white/15 bg-white/10 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-2 text-base font-bold">
                <BadgeCheck className="h-5 w-5 text-emerald-300" aria-hidden="true" />
                숨은보험금 찾아보시겠습니까?
              </div>
              <p className="mt-2 text-sm leading-6 text-slate-300">
                고객이 상담을 선택하면 리드 DB로 전환하고 담당자가 후속 안내를 진행합니다.
              </p>
            </div>
            <button type="button" className="inline-flex items-center justify-center gap-2 rounded bg-white px-5 py-3 text-sm font-bold text-slate-950 transition hover:bg-amber-50">
              상담 신청
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </section>
    </main>
  )
}
